import { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation, useNavigationType } from 'react-router-dom';
import { getItemGroupPageData } from '../api/cmsApi';
import { fetchNavigationTree } from '../../../api/apiNavigation';

import TextHeader from '../../../components/textHeader/TextHeader';
import FilterComponent from '../../../components/shared/filter/FilterComponent';
import LoadingScreen from '../../../components/shared/loading/LoadingScreen';
import MissingPage from '../../../components/shared/errorMessages/MissingPage';
import { captureAnalytics } from '../../../utilities/Analytics';
import { CMSPageData, ChildTileData } from '../types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../components/banner/CMSConsumerCarouselBanner';
import CMSConsumerApplicationsGrid from '../components/grid/CMSConsumerApplicationsGrid';
import CMSConsumerFeaturedGallery from '../components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerNewArrivalsGrid from '../components/grid/CMSConsumerNewArrivalsGrid';

function CategoryTemplate() {
  const { item_group } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();

  // Create scroll position management
  const scrollPositionKey = `category_scroll_${item_group}`;
  const scrollRestored = useRef(false);

  // CMS Data Query - Consumer always uses live mode
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-item-group-${item_group}`],
    () => getItemGroupPageData(item_group || '', 'live'), // Always use live mode for consumer
    {
      staleTime: 600000, // Cache data for 10 minutes
      enabled: !!item_group, // Only run if item_group exists
    }
  );

  // Fetch navigation tree to get nav_add_on items
  const { data: navTreeData } = useQuery(
    'navigationTree',
    fetchNavigationTree,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Get the children data (applications/categories) - always use active data
  let childrenData = cmsData?.children_tile_data?.active || [];

  // Merge nav_add_on items from navigation tree if this is an item_group page
  if (item_group && navTreeData) {
    const currentItemGroup = navTreeData.find(
      (node) => node.item_group === item_group && node.category_type === 'item_group'
    );

    if (currentItemGroup?.children) {
      const navAddOns = currentItemGroup.children
        .filter(
          (child) =>
            child.category_type === 'nav_add_on' &&
            child.is_active === true &&
            child.consumer_tile_display_switch === true &&
            (child.consumer_link_url || child.consumer_link_category_id) &&
            child.consumer_tile_media_single_url
        )
        .map((child): ChildTileData => ({
          // Map NavTreeNode to ChildTileData format
          id: parseInt(child.id) || 0,
          category_id: child.category_id || child.id,
          page_title: child.display_name || child.category_name,
          description: null,
          tile_display_media_single: child.consumer_tile_media_single || null,
          tile_display_media_single_link: child.consumer_tile_media_single_url || '',
          tile_display_media_carousel: [],
          tile_display_media_option: 'single',
          item_group: child.item_group || '',
          item_group_name: '',
          application: child.application || '',
          brand: child.brand || '',
          collection: child.collection || '',
          category_name: child.category_name,
          category_type: child.category_type,
          is_published: true,
          position_order: child.position_order ?? 999,
          // Include nav_add_on specific fields
          display_name: child.display_name,
          is_active: child.is_active,
          consumer_use_switch: child.consumer_use_switch,
          consumer_link_url: child.consumer_link_url,
          consumer_link_category_id: child.consumer_link_category_id,
          consumer_tile_display_switch: child.consumer_tile_display_switch,
          consumer_tile_hover_animation_switch: child.consumer_tile_hover_animation_switch,
          consumer_tile_media_single: child.consumer_tile_media_single,
          consumer_tile_media_single_url: child.consumer_tile_media_single_url,
        }));

      // Merge and sort by position_order
      childrenData = [...childrenData, ...navAddOns].sort((a, b) => {
        const orderA = a.position_order ?? 999;
        const orderB = b.position_order ?? 999;
        return orderA - orderB;
      });
    }
  }

  // Get the new arrivals data and filter published items only
  const rawNewArrivalsData = cmsData?.new_arrivals?.active || [];
  const newArrivalsData = rawNewArrivalsData
    .filter(item => item.is_published) // Consumer only sees published items
    .sort((a, b) => {
      const orderA = a.order_position || 999;
      const orderB = b.order_position || 999;
      return orderA - orderB;
    });

  // Check if this is homeware (item_group 000)
  const isHomeware = item_group === '000';

  // Fixed scroll management - only scroll to top on new navigations, not when returning
  useEffect(() => {
    // Returning from a PDP: FilterComponent restores the exact scroll position
    // (and re-loads enough items in "Show All"), so don't interfere here.
    if (navigationType === 'POP' && sessionStorage.getItem('multifilter_restore')) {
      return;
    }
    if (navigationType !== 'POP') {
      // New navigation, scroll to top and clear saved position
      window.scrollTo(0, 0);
      sessionStorage.removeItem(scrollPositionKey);
      scrollRestored.current = false;
    } else if (!scrollRestored.current) {
      // Coming back, try to restore position only once
      const savedPosition = sessionStorage.getItem(scrollPositionKey);
      if (savedPosition) {
        const targetPosition = parseInt(savedPosition, 10);

        // Wait for content to load before restoring scroll
        const restoreScroll = () => {
          window.scrollTo(0, targetPosition);
          scrollRestored.current = true;
        };

        // Try immediate restore
        restoreScroll();

        // Also try after content loads
        const timeoutId = setTimeout(restoreScroll, 100);
        const finalTimeout = setTimeout(restoreScroll, 500);

        return () => {
          clearTimeout(timeoutId);
          clearTimeout(finalTimeout);
        };
      } else {
        // No saved position, ensure we're at top
        window.scrollTo(0, 0);
        scrollRestored.current = true;
      }
    }
  }, [navigationType, scrollPositionKey, cmsSuccess]);

  // Save scroll position when leaving the page - only save if we're not restoring
  useEffect(() => {
    const handleScroll = () => {
      // Only save scroll position if we're not in the middle of restoring
      if (scrollRestored.current) {
        sessionStorage.setItem(scrollPositionKey, window.scrollY.toString());
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollPositionKey]);

  // Handle window resize events - more conservative approach
  useEffect(() => {
    const handleResize = () => {
      // Defer to FilterComponent's restore when returning from a PDP.
      if (sessionStorage.getItem('multifilter_restore')) return;
      // Only restore on resize if we have a saved position and have successfully restored before
      if (scrollRestored.current && navigationType === 'POP') {
        const savedPosition = sessionStorage.getItem(scrollPositionKey);
        if (savedPosition) {
          window.scrollTo(0, parseInt(savedPosition, 10));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollPositionKey, navigationType]);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData) {
      const title = cmsData.page_title;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, item_group, cmsSuccess, cmsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Page Header */}
          <TextHeader
            heading={cmsData?.page_title || 'Loading...'}
            content={cmsData?.description || ''}
            isCentered={false}
            width='70%'
            marginTop={10}
            marginBottom={5}
          />

          {/* CMS Consumer Applications Grid - Replaces old ApplicationsGrid and HomewareCategoryGrid */}
          <CMSConsumerApplicationsGrid
            childrenData={childrenData}
            spacingValue={4}
            marginTop={15}
            marginBottom={15}
            isHomeware={isHomeware}
          />

          {/* CMS Consumer Featured Gallery - Only show if enabled and has data */}
          {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
            <CMSConsumerFeaturedGallery
              featuredCollection={cmsData.featured_collection}
              hasVideo={false}
              invert={false}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* CMS Consumer New Arrivals - Only show if has data */}
          {newArrivalsData.length > 0 && (
            <CMSConsumerNewArrivalsGrid
              newArrivalsData={newArrivalsData}
              header="New arrivals"
              hasTitles={true}
              hasButtons={true}
              hasBackgroundText={false}
              spacingValue={4}
              marginTop={'5rem'}
              marginBottom={'5rem'}
              viewMode="live"
            />
          )}

          {/* Filter Component - Only show for non-homeware categories */}
          {item_group !== '000' && (
            <FilterComponent
              itemGroup={item_group}
              title={cmsData?.page_title || ''}
              initialApplications={[]}
            />
          )}
        </>
      )}
    </>
  );
}

export default CategoryTemplate;